import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Box, Button, Typography, Stack, Divider, useTheme, Fade, Zoom, Container, TextField, InputAdornment,
    IconButton, Tooltip, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, styled,
    Autocomplete, Chip, TableContainer, Paper, Breadcrumbs, Link,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CallIcon from '@mui/icons-material/Call';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import HomeIcon from '@mui/icons-material/Home';
import ExcelJS from 'exceljs';

const EnquiryReport = () => {
    const theme = useTheme();
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
    const [toDate, setToDate] = useState(dayjs());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [followBy, setFollowBy] = useState([]);
    const [selectedFollowByUserId, setSelectedFollowByUserId] = useState(null);
    const [searchText, setSearchText] = useState('');
    const contentRef = useRef();
    const globalProperty = useSelector(selectGlobalProperty);
    const permissions = usePermissions("Refund Register");
    const { ViewRec = "0" } = permissions || {};

    const navigate = useNavigate();
    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `Refund Register`,
        pageStyle: `
        @page { 
            size: A4 portrait; 
            margin: 1.2cm;
            @top-center { content: "Refund Register Report"; font-weight: bold; font-size: 24px; color: #635bff }
            @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 10px; }
        }
        @media print {
            body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact; 
            }
            .no-print { display: none !important; }
            .print-header { 
                text-align: center; 
                margin-bottom: 16px; 
                page-break-after: avoid;
            }
            .print-title { 
                font-size: 18px; 
                font-weight: bold; 
                margin: 4px 0; 
                color: #635bff;
            }
            .print-subtitle { 
                font-size: 12px; 
                color: #555; 
                margin: 3px 0;
            }
            table { 
                width: 100%; 
                border-collapse: collapse; 
                font-size: 11px; 
                page-break-inside: avoid;
            }
            th { 
                background: #635bff !important; 
                color: white !important; 
                padding: 3px 6px; 
                font-weight: bold;
                text-align: left;
            }
            td { 
                padding: 5px 8px; 
                border-bottom: 1px solid #eee;
            }
            tr:nth-child(even) td { 
                background-color: #f9f9f9; 
            }
            .total-row td { 
                background: #e8f5e9 !important; 
                font-weight: bold; 
                color: #1b5e20;
            }
            .net-amount { 
                font-weight: 700; 
                color: #e91e63; 
            }

             /* Show the total row only on the last page */
            @page :last {
                .total-row {
                    display: table-row !important;
                }
            }

            /* Hide total row from all other pages except the last page */
            @media print {
                .total-row {
                    display: none !important;
                }

                /* Show only on the last page */
                @page :last {
                    .total-row {
                        display: table-row !important;
                    }
                }
            }
        }
    `,
    });

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('TrnEnq/GetTrnEnqReport?currentPage=1&limit=15', {
                PropId: globalProperty,
                EnqDt: fromDate.format('YYYY-MM-DD'),
                LastFlwDt: toDate.format('YYYY-MM-DD'),
                FlwUserId: selectedFollowByUserId || 1
            });
            if (response.data.Status === 0) {
                setRecords(response.data.Data || []);
                setFilteredRecords(response.data.Data || []);
            } else {
                setRecords([]);
                setFilteredRecords([]);
            }
        } catch (error) {
            toast.error('Error fetching records from the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchFollowBy = async () => {
            try {
                const response = await axiosInstance.post('TrnFlwup/TrnFlwupFlwUser', {
                    PropId: globalProperty,
                    Flag: ""
                });
                if (response.data.Status === 0) {
                    setFollowBy(response.data.Data);
                } else {
                    setFollowBy([]);
                }
            } catch (error) {
                toast.error("Error while fetching response.");
            }
        };

        fetchFollowBy();
    }, [])

    useEffect(() => {
        if (globalProperty) {
            fetchRecords();
        }
    }, [globalProperty]);

    useEffect(() => {
        const searchWords = searchText.trim().toLowerCase().split(/\s+/);
        const filtered = records.filter((refReg) => {
            const sourceName = refReg.SourceName?.toLowerCase() || '';
            const enqType = refReg.EnqTypeNm?.toLowerCase() || '';
            const enqby = refReg.EnqBy?.toLowerCase() || '';
            const propName = refReg.PropName?.toLowerCase() || '';
            const srcby = refReg.SourceBy?.toLowerCase() || '';
            const remark = refReg.Remark?.toLowerCase() || '';
            const empnm = refReg.FlwEmpNm?.toLowerCase() || '';
            const enqSt = refReg.EnqStatus?.toLowerCase() || '';
            const enqNo = refReg.EnqNo?.toLowerCase() || '';
            const mobileNo = refReg.MobileNo?.toLowerCase() || '';
            const bookNo = refReg.BookNo?.toLowerCase() || '';
            const enqDate = dayjs(refReg.EnqDt).format('DD-MM-YYYY').toLowerCase();
            const dueDate = dayjs(refReg.ChkOutDt).format('DD-MM-YYYY').toLowerCase();

            return searchWords.every((word) => {
                return (
                    sourceName.includes(word) ||
                    enqType.includes(word) ||
                    enqby.includes(word) ||
                    enqSt.includes(word) ||
                    srcby.includes(word) ||
                    remark.includes(word) ||
                    propName.includes(word) ||
                    empnm.includes(word) ||
                    enqNo.includes(word) ||
                    mobileNo.includes(word) ||
                    bookNo.includes(word) ||
                    enqDate.includes(word) ||
                    dueDate.includes(word)
                );
            });
        });

        setFilteredRecords(filtered);
    }, [searchText, records]);

    const handleLoadRecords = () => {
        fetchRecords();
    };

    const handleExportToExcel = async () => {
        if (!filteredRecords.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Enquiry Report');

        const headers = [
            'Property', 'EnqNo', 'EnqDt', 'EnqStatus', 'MobileNo', 'SourceName', 'SourceBy',
            'CreatedBy', 'EnqBy', 'NextFlwDt', 'VisitDt', 'CanlDt', 'VisitSt', 'VisitEmpNm',
            'VisitWith', 'RoomType', 'OcupntMob', 'FollowBy', 'LastFollowBy', 'Remark',
            'Resident', 'ChkOutDt', 'BookNo',
        ];

        const headerRow = worksheet.addRow(headers);

        // Header style
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '635BFF' } };
        });

        // Data rows
        filteredRecords.forEach((refReg) => {
            const rowValues = [
                refReg.PropName || '-',
                refReg.EnqNo || '-',
                refReg.EnqDt ? dayjs(refReg.EnqDt).format('DD-MM-YYYY') : '-',
                refReg.EnqStatus || '-',
                refReg.MobileNo || '-',
                refReg.SourceName || '-',
                refReg.SourceBy || '-',
                refReg.EnqTpName || '-',
                refReg.EnqBy || '-',
                refReg.NextFlwDt ? dayjs(refReg.NextFlwDt).format('DD-MM-YYYY') : '-',
                refReg.VisitDt ? dayjs(refReg.VisitDt).format('DD-MM-YYYY') : '-',
                refReg.CanlDt ? dayjs(refReg.CanlDt).format('DD-MM-YYYY') : '-',
                refReg.VisitSt === 'Y' ? 'Yes' : refReg.VisitSt === 'N' ? 'No' : '-',
                refReg.VisitEmpNm || refReg.VisitWith || '-',
                refReg.VisitEmpNm || refReg.VisitWith || '-',
                refReg.RoomTypeName || '-',
                refReg.OcupntMob || '-',
                refReg.FlwEmpNm || '-',
                refReg.LastFlwUserName || '-',
                refReg.Remark || '-',
                refReg.ResidentName || '-',
                refReg.ChkOutDt ? dayjs(refReg.ChkOutDt).format('DD-MM-YYYY') : '-',
                refReg.BookNo || '-',
            ];

            const row = worksheet.addRow(rowValues);

            // Color entire row based on EnqStatus
            const status = (refReg.EnqStatus || '').toLowerCase();
            let fillColor = 'FFFFFFFF'; // default white

            if (status === 'open') fillColor = 'FFFFF2CC'; // light yellow
            else if(status === 'cancel') fillColor = 'FFFFA3A3'; // Red
            else if (status === 'close' || status === 'resolved') fillColor = 'FFB6D7B2';

            row.eachCell((cell) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fillColor } };
            });
        });

        // Column widths
        worksheet.columns.forEach((col) => {
            col.width = 16;
        });

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `Enquiry_${dayjs(fromDate).format('DDMMYYYY')}_to_${dayjs(toDate).format('DDMMYYYY')}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    return (
        <>
            <Box sx={{ backgroundColor: 'rgb(249, 249, 249)', }}>
                <Box
                    sx={{
                        backgroundColor: "#ffffff",
                        padding: { xs: "8px 16px", sm: "12px 16px" },
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                        borderRadius: "12px",
                        margin: "10px auto",
                        maxWidth: "92%",
                        flexDirection: { xs: "column", sm: "column", md: "row" },
                        gap: 2,
                    }}
                >
                    <Breadcrumbs
                        aria-label="breadcrumb"
                        separator="›"
                        sx={{
                            fontSize: { xs: "14px", sm: "16px" },
                            display: "flex",
                            flexDirection: { xs: "column", sm: "column", md: "row" }
                        }}
                    >
                        <Link
                            underline="hover"
                            color="#635bff"
                            sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                            onClick={() => navigate('/dashboard')}
                        >
                            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                            <Box component="span">Home</Box>
                        </Link>
                        <Link
                            underline="hover"
                            color="#635bff"
                            sx={{ cursor: "pointer", fontWeight: "500" }}
                            onClick={() => navigate('/reports/dailysummaryreport')}
                        >
                            Report
                        </Link>

                        <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                            Enquiry Report
                        </Typography>
                        <Chip
                            size="small"
                            label={records.length}
                            sx={{
                                marginLeft: 2,
                                background: '#635bff',
                                color: 'white',
                                fontWeight: 'bold',
                                borderRadius: '16px',
                                padding: '4px 12px',
                            }}
                        />
                    </Breadcrumbs>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mt: { xs: 1, sm: 1, md: 0 },
                            flexShrink: 0,
                        }}
                    >
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                sx={{
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                    alignItems: { xs: 'stretch', md: 'center' },
                                    width: '100%',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'space-between', md: 'flex-start' },
                                        flexWrap: { xs: 'nowrap', md: 'nowrap' },
                                    }}
                                >
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(newValue) => setFromDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    flex: { xs: '0 1 48%', md: '0 1 160px' },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                                },
                                            },
                                        }}
                                        maxDate={toDate}
                                        format="DD/MM/YYYY"
                                    />
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        onChange={(newValue) => setToDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                sx: {
                                                    flex: { xs: '0 1 48%', md: '0 1 160px' },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                                },
                                            },
                                        }}
                                        minDate={fromDate}
                                        format="DD/MM/YYYY"
                                    />
                                </Stack>

                                <Autocomplete
                                    id="follow-by-filter"
                                    options={followBy}
                                    getOptionLabel={(option) => option.UserName || ""}
                                    value={followBy.find((user) => user.FlwUserId === selectedFollowByUserId) || null}
                                    onChange={(event, newValue) => {
                                        setSelectedFollowByUserId(newValue?.FlwUserId || null);
                                    }}
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Follow By" variant="outlined" />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.FlwUserId === value?.FlwUserId}
                                />

                                <Stack direction="row" spacing={1}
                                    sx={{
                                        mt: { xs: 1, md: 0 },
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        width: { xs: '100%', md: 'auto' },
                                    }}
                                >
                                    <Tooltip title='Retrieve data' arrow>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleLoadRecords}
                                                disabled={!fromDate || !toDate}
                                                sx={{
                                                    height: 30,
                                                    bgcolor: '#635bff',
                                                    color: 'white',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        bgcolor: '#635bff',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Get Data
                                            </Button>
                                        </span>
                                    </Tooltip>

                                    <Tooltip title='Export to excel' arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImportExportIcon />}
                                                onClick={handleExportToExcel}
                                                disabled={records.length === 0}
                                                sx={{
                                                    height: 30,
                                                    borderColor: 'success.main',
                                                    color: 'success.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        borderColor: 'success.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Export
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </LocalizationProvider>
                        <Tooltip title='Previous Page' arrow>
                            <Box sx={{
                                display: "flex", alignItems: "center", gap: 1,
                                justifyContent: "flex-start"
                            }}
                            >
                                <IconButton
                                    onClick={() => navigate('/reports/dailysummaryreport')}
                                    sx={{
                                        backgroundColor: '#635bff',
                                        borderRadius: '50%',
                                        width: 35,
                                        height: 35,
                                        '&:hover': {
                                            backgroundColor: '#635bff',
                                        },
                                        boxShadow: 3,
                                    }}
                                >
                                    <ArrowForwardIcon sx={{ color: 'white' }} />
                                </IconButton>
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>

                <Container
                    maxWidth={false}
                    sx={{
                        width: '92%',
                        maxWidth: 'calc(100vw - 80px)',
                        mt: 2,
                        p: 3,
                        pb: 1,
                        bgcolor: 'grey.50',
                        borderRadius: 4,
                        boxShadow: theme.shadows[10],
                        mx: 'auto',
                        overflowX: 'hidden',
                    }}
                >
                    <ToastContainer />


                    <Box sx={{ displayPrint: 'none' }}>
                        <Box sx={{ mb: 1 }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search by Enquiry, Mobile No, FollowBy, etc."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                        </InputAdornment>
                                    ),
                                    sx: {
                                        bgcolor: 'white',
                                        borderRadius: 2,
                                        '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.8 },
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'primary.dark',
                                            boxShadow: theme.shadows[2],
                                        },
                                    },
                                }}
                                sx={{ maxWidth: '100vw', mx: 'auto' }}
                            />
                        </Box>

                        <Divider sx={{ mb: 1, borderColor: 'grey.200', borderWidth: 1 }} />

                        {loading ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <CircularProgress size="3rem" sx={{ mb: 1 }} />
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    Loading data...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                {filteredRecords.length === 0 ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                                        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                                            No records found
                                        </Typography>
                                    </Box>
                                ) : (
                                    <TableContainer
                                        component={Paper}
                                        sx={{
                                            maxHeight: { sm: 350, md: 450, lg: 500 },
                                            width: '100%',
                                            overflowX: 'auto',
                                            overflowY: 'auto',
                                            whiteSpace: 'nowrap',
                                            boxShadow: 3
                                        }}
                                    >
                                        <Table
                                            stickyHeader
                                            size="small"
                                            sx={{
                                                minWidth: 1000,
                                                '& .MuiTableCell-root': {
                                                    py: 0.7,
                                                    px: 1,
                                                    fontSize: '0.8rem'
                                                }
                                            }}
                                        >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={cellStyle}><b>Property</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>EnqNo</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>EnqDt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>EnqSt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>Mobile</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>Source</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>SourceBy</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>EnqType</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>EnqBy</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>CreatedBy</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>NextFlwDt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>VisitDt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>CanlDt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>VisitSt</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>VisitWith</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>RoomType</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>OccupantMob</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>FollowBy</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>LastFlwBy</b></TableCell>
                                                    <TableCell sx={cellStyle}><b>Remark</b></TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {filteredRecords.map((refReg) => (
                                                    <TableRow
                                                        key={refReg.id}
                                                        hover
                                                        sx={{
                                                            '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }
                                                        }}
                                                    >
                                                        <TableCell>{refReg.PropName}</TableCell>
                                                        <TableCell>{refReg.EnqNo}</TableCell>
                                                        <TableCell>{dayjs(refReg.EnqDt).format('DD-MM-YYYY')}</TableCell>
                                                        <TableCell>{refReg.EnqStatus}</TableCell>
                                                        <TableCell>{refReg.MobileNo}</TableCell>
                                                        <TableCell>{refReg.SourceName}</TableCell>
                                                        <TableCell>{refReg.SourceBy}</TableCell>
                                                        <TableCell>{refReg.EnqTypeNm}</TableCell>
                                                        <TableCell>{refReg.EnqBy}</TableCell>
                                                        <TableCell>{refReg.EnqTpName}</TableCell>
                                                        <TableCell>{dayjs(refReg.NextFlwDt).format('DD-MM-YYYY')}</TableCell>
                                                        <TableCell>{dayjs(refReg.VisitDt).format('DD-MM-YYYY')}</TableCell>
                                                        <TableCell> {dayjs(refReg.CanlDt).format('DD-MM-YYYY')} </TableCell>
                                                        <TableCell>{refReg.VisitSt === 'Y' ? 'Yes' : 'No'}</TableCell>
                                                        <TableCell>{refReg.VisitEmpNm}</TableCell>
                                                        <TableCell>{refReg.RoomTypeName}</TableCell>
                                                        <TableCell>{refReg.OcupntMob}</TableCell>
                                                        <TableCell>{refReg.FlwEmpNm}</TableCell>
                                                        <TableCell>{refReg.LastFlwUserName}</TableCell>
                                                        <TableCell
                                                            sx={{
                                                                maxWidth: 120,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                        >
                                                            <Tooltip title={refReg.Remark} arrow>
                                                                <span>{refReg.Remark}</span>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </>
                        )}
                    </Box>
                </Container >
            </Box>
        </>
    );
};

const cellStyle = {
    background: '#635bff',
    color: '#ffffff',
};

export default EnquiryReport;
